<template>
  <div
    v-if="mobileView"
    @mouseenter="renderDropdown = true"
    @touchstart="renderDropdown = true"
  >
    <div class="flex space-x-2 items-end">
      <div class="group/contenteditable-container bg-base-100 rounded-md p-2 border border-base-300 w-full flex justify-between items-end">
        <div class="flex items-center space-x-2">
          <span
            class="w-3 h-3 flex-shrink-0 rounded-full"
            :class="colors[submitters.indexOf(selectedSubmitter)]"
          />
          <Contenteditable
            v-model="selectedSubmitter.name"
            class="cursor-text"
            :icon-inline="true"
            :editable="editable"
            :select-on-edit-click="true"
            :icon-width="18"
            @update:model-value="$emit('name-change', selectedSubmitter)"
          />
        </div>
      </div>
      <div class="dropdown dropdown-top dropdown-end">
        <label
          tabindex="0"
          class="bg-base-100 cursor-pointer rounded-md p-2 border border-base-300 w-full flex justify-center"
        >
          <IconChevronUp
            width="24"
            height="24"
          />
        </label>
        <ul
          v-if="editable && renderDropdown"
          tabindex="0"
          class="rounded-md min-w-max mb-2"
          :class="menuClasses"
          :style="menuStyle"
          @click="closeDropdown"
        >
          <li
            v-for="(submitter, index) in submitters"
            :key="submitter.uuid"
          >
            <a
              href="#"
              data-compact-party-option
              class="relative flex min-h-[34px] overflow-hidden border px-2 group justify-between items-center rounded-md"
              :class="submitter === selectedSubmitter ? `${partyTintClasses[index]} border-base-300` : 'bg-white border-base-300'"
              @click.prevent="selectSubmitter(submitter)"
            >
              <span
                class="absolute inset-y-0 left-0 w-2"
                :class="partyRailClasses[index]"
              />
              <span class="py-1 pl-3 flex items-center">
                <span>{{ partyDisplayName(submitter.name) }}</span>
              </span>
              <button
                v-if="submitters.length > 1 && editable"
                class="px-2"
                @click.prevent.stop="remove(submitter)"
              >
                <IconTrash :width="18" />
              </button>
            </a>
          </li>
          <li v-if="submitters.length < names.length && editable">
            <a
              href="#"
              class="flex px-2"
              @click.prevent="addSubmitter"
            >
              <IconUserPlus
                :width="20"
                :stroke-width="1.6"
              />
              <span class="py-1">
                {{ t('add') }} {{ names[lastPartyIndex] }}
              </span>
            </a>
          </li>
        </ul>
      </div>
    </div>
  </div>
  <div
    v-else
    class="dropdown"
    @mouseenter="renderDropdown = true"
    @touchstart="renderDropdown = true"
  >
    <div
      v-if="!compact"
      data-party-list
      class="space-y-1"
    >
      <div
        v-for="(submitter, index) in submitters"
        :key="submitter.uuid"
        role="group"
        tabindex="0"
        :aria-label="`Select ${submitter.name}`"
        class="group/party relative flex min-h-[38px] items-center overflow-hidden rounded-md border transition-colors"
        :class="submitter === selectedSubmitter ? `${partyTintClasses[index]} border-base-300` : 'bg-white border-base-300 hover:bg-base-100'"
        @click="selectSubmitter(submitter)"
        @keydown.enter.self.prevent="selectSubmitter(submitter)"
        @keydown.space.self.prevent="selectSubmitter(submitter)"
      >
        <span
          class="absolute inset-y-0 left-0 w-2"
          :class="partyRailClasses[index]"
        />
        <div class="group/contenteditable-container flex min-w-0 flex-1 items-center pl-4 pr-1 py-1.5">
          <Contenteditable
            v-if="editingSubmitterUuid === submitter.uuid"
            ref="submitterEditor"
            v-model="submitter.name"
            class="min-w-0 flex-1 cursor-text"
            :icon-inline="false"
            :editable="editable"
            :select-on-edit-click="true"
            @click.stop
            @blur="editingSubmitterUuid = null"
            @update:model-value="$emit('name-change', submitter)"
          />
          <span v-else class="min-w-0 flex-1 truncate text-sm">
            <template v-if="partyDisplayParts(submitter.name)">
              {{ partyDisplayParts(submitter.name).number }}<sup>{{ partyDisplayParts(submitter.name).suffix }}</sup> {{ partyDisplayParts(submitter.name).label }}
            </template>
            <template v-else>
              {{ submitter.name }}
            </template>
          </span>
          <button
            v-if="editable"
            :title="t('edit')"
            class="ml-1 rounded p-1 transition-opacity group-hover/party:opacity-100"
            :class="submitter === selectedSubmitter ? 'opacity-100' : 'opacity-0'"
            @click.prevent.stop="startEditingSubmitter(submitter)"
          >
            <IconEdit :width="16" />
          </button>
        </div>
        <div v-if="submitters.length > 1 && editable" class="flex items-center pr-1">
          <button
            :title="t('remove')"
            class="rounded p-1 transition-opacity group-hover/party:opacity-100"
            :class="submitter === selectedSubmitter ? 'opacity-100' : 'opacity-0'"
            @click.prevent.stop="remove(submitter)"
          >
            <IconTrash :width="17" />
          </button>
        </div>
      </div>
      <button
        v-if="submitters.length < names.length && editable && allowAddNew"
        class="flex w-full items-center justify-center gap-1.5 rounded-md border border-dashed border-base-300 bg-white px-3 py-2 text-sm hover:bg-base-100"
        @click.prevent="addSubmitter"
      >
        <IconUserPlus :width="18" :stroke-width="1.6" />
        {{ t('add_new_party') }}
      </button>
    </div>
    <label
      v-if="compact"
      data-compact-party-trigger
      tabindex="0"
      :title="selectedSubmitter?.name"
      class="cursor-pointer flex min-h-[34px] h-full self-stretch items-stretch"
    >
      <span
        class="block h-full w-2 rounded-l"
        :class="partyRailClasses[submitters.indexOf(selectedSubmitter)]"
      />
    </label>
    <ul
      v-if="compact && renderDropdown"
      tabindex="0"
      class="space-y-1 p-1"
      :class="menuClasses"
      :style="menuStyle"
      @click="closeDropdown"
    >
      <li
        v-for="(submitter, index) in submitters"
        :key="submitter.uuid"
      >
        <a
          href="#"
          data-compact-party-option
          class="relative flex min-h-[34px] overflow-hidden border px-2 group justify-between items-center rounded-md"
          :class="submitter === selectedSubmitter ? `${partyTintClasses[index]} border-base-300` : 'bg-white border-base-300'"
          @click.prevent="selectSubmitter(submitter)"
        >
          <span
            class="absolute inset-y-0 left-0 w-2"
            :class="partyRailClasses[index]"
          />
          <span class="py-1 pl-3 flex items-center">
            <span>{{ partyDisplayName(submitter.name) }}</span>
          </span>
        </a>
      </li>
      <li
        v-if="submitters.length < names.length && editable && allowAddNew"
        class="mt-1 border-t border-base-200 pt-1"
      >
        <a
          href="#"
          class="flex px-2"
          @click.prevent="addSubmitter"
        >
          <IconUserPlus
            :width="20"
            :stroke-width="1.6"
          />
          <span class="py-1">
            {{ t('add') }} {{ names[lastPartyIndex] }}
          </span>
        </a>
      </li>
    </ul>
  </div>
</template>

<script>
import { IconUserPlus, IconTrash, IconChevronUp, IconEdit } from '@tabler/icons-vue'
import Contenteditable from './contenteditable'
import { partyDisplayName, partyDisplayParts } from './party_display_name.mjs'
import { v4 } from 'uuid'

export default {
  name: 'FieldSubmitter',
  components: {
    IconUserPlus,
    Contenteditable,
    IconTrash,
    IconEdit,
    IconChevronUp
  },
  inject: ['t', 'save'],
  props: {
    submitters: {
      type: Array,
      required: true
    },
    editable: {
      type: Boolean,
      required: false,
      default: true
    },
    compact: {
      type: Boolean,
      required: false,
      default: false
    },
    mobileView: {
      type: Boolean,
      required: false,
      default: false
    },
    allowAddNew: {
      type: Boolean,
      required: false,
      default: true
    },
    modelValue: {
      type: String,
      required: true
    },
    menuStyle: {
      type: Object,
      required: false,
      default: () => ({ backgroundColor: 'white' })
    },
    menuClasses: {
      type: String,
      required: false,
      default: 'dropdown-content menu p-2 shadow rounded-box w-full z-10 bg-white!'
    }
  },
  emits: ['update:model-value', 'remove', 'new-submitter', 'name-change'],
  data () {
    return {
      renderDropdown: false,
      editingSubmitterUuid: null
    }
  },
  computed: {
    partyRailClasses () {
      return [
        'bg-red-200', 'bg-sky-200', 'bg-emerald-200', 'bg-yellow-200', 'bg-purple-200',
        'bg-pink-200', 'bg-cyan-200', 'bg-orange-200', 'bg-lime-200', 'bg-indigo-200',
        'bg-red-200', 'bg-sky-200', 'bg-emerald-200', 'bg-yellow-200', 'bg-purple-200',
        'bg-pink-200', 'bg-cyan-200', 'bg-orange-200', 'bg-lime-200', 'bg-indigo-200'
      ]
    },
    partyTintClasses () {
      return [
        'bg-red-50', 'bg-sky-50', 'bg-emerald-50', 'bg-yellow-50', 'bg-purple-50',
        'bg-pink-50', 'bg-cyan-50', 'bg-orange-50', 'bg-lime-50', 'bg-indigo-50',
        'bg-red-50', 'bg-sky-50', 'bg-emerald-50', 'bg-yellow-50', 'bg-purple-50',
        'bg-pink-50', 'bg-cyan-50', 'bg-orange-50', 'bg-lime-50', 'bg-indigo-50'
      ]
    },
    colors () {
      return [
        'bg-red-500',
        'bg-sky-500',
        'bg-emerald-500',
        'bg-yellow-300',
        'bg-purple-600',
        'bg-pink-500',
        'bg-cyan-500',
        'bg-orange-500',
        'bg-lime-500',
        'bg-indigo-500',
        'bg-red-500',
        'bg-sky-500',
        'bg-emerald-500',
        'bg-yellow-300',
        'bg-purple-600',
        'bg-pink-500',
        'bg-cyan-500',
        'bg-orange-500',
        'bg-lime-500',
        'bg-indigo-500'
      ]
    },
    names () {
      return [
        this.t('first_party'),
        this.t('second_party'),
        this.t('third_party'),
        this.t('fourth_party'),
        this.t('fifth_party'),
        this.t('sixth_party'),
        this.t('seventh_party'),
        this.t('eighth_party'),
        this.t('ninth_party'),
        this.t('tenth_party'),
        this.t('eleventh_party'),
        this.t('twelfth_party'),
        this.t('thirteenth_party'),
        this.t('fourteenth_party'),
        this.t('fifteenth_party'),
        this.t('sixteenth_party'),
        this.t('seventeenth_party'),
        this.t('eighteenth_party'),
        this.t('nineteenth_party'),
        this.t('twentieth_party')
      ]
    },
    lastPartyIndex () {
      const index = Math.max(...this.submitters.map((s) => this.names.indexOf(s.name)))

      if (index !== -1) {
        return index + 1
      } else {
        return this.submitters.length
      }
    },
    selectedSubmitter () {
      return this.submitters.find((e) => e.uuid === this.modelValue)
    }
  },
  methods: {
    partyDisplayName,
    partyDisplayParts,
    startEditingSubmitter (submitter) {
      this.editingSubmitterUuid = submitter.uuid

      this.$nextTick(() => this.$refs.submitterEditor?.[0]?.focusContenteditable())
    },
    selectSubmitter (submitter) {
      this.$emit('update:model-value', submitter.uuid)
    },
    remove (submitter) {
      if (window.confirm(this.t('are_you_sure_'))) {
        this.$emit('remove', submitter)
      }
    },
    addSubmitter () {
      const newSubmitter = {
        name: this.names[this.lastPartyIndex],
        uuid: v4()
      }

      this.submitters.push(newSubmitter)

      this.$emit('update:model-value', newSubmitter.uuid)
      this.$emit('new-submitter', newSubmitter)
    },
    closeDropdown () {
      document.activeElement.blur()
    }
  }
}
</script>
